import type { Quiz } from "./types";

/**
 * A knowledge quiz, which means schema.org `Quiz` and a duty of accuracy.
 *
 * Every question below has one defensible answer and a stated reason, and
 * several of them are the ones people get wrong because the popular version has
 * drifted from the text — no number of wise men, no apple, no innkeeper. That
 * is the whole editorial idea: a trivia page nobody learns anything from is a
 * page nobody links to.
 */
export const article: Quiz = {
  slug: "bible-knowledge-quiz",
  kind: "knowledge",
  title: "Bible quiz: 15 questions most people get wrong",
  h1: "The Bible quiz that checks what you actually read",
  description:
    "Fifteen questions on what scripture says, rather than what everyone remembers it saying — the number of wise men, the fruit in Eden, the fish, and the root of all evil.",
  targets: [
    "bible quiz",
    "bible trivia questions and answers",
    "bible knowledge test",
    "things people get wrong about the bible",
  ],
  published: "2026-08-26",
  standfirst:
    "Fifteen questions, one correct answer each, and a reason after every one. Most of the misses are things everybody knows that are not in the text.",
  takeaways: [
    "Scripture never gives a number of wise men, never calls the fruit an apple, never mentions an innkeeper, and says the love of money is a root of all kinds of evil rather than the root of all evil.",
    "Jonah is swallowed by a great fish; the Hebrew does not say whale, and neither does the Greek quoted in Matthew.",
    "Eight people were on the ark, Peter denied Jesus three times, and Psalms is the book with the most chapters at 150.",
    "Getting these wrong is normal — most of them come from carols, paintings and repetition rather than from careless reading.",
  ],
  intro: [
    {
      type: "p",
      text: "Almost every question below has a popular answer that is confidently held, widely repeated and not in the Bible. That is not a failure of anyone's attention. It is what happens when a text is filtered through carols, nativity plays, Renaissance paintings and two thousand years of people summarising each other — the summary is easier to remember than the passage, and after a while the summary is what everyone means.",
    },
    {
      type: "p",
      text: "So this is less a test than an audit. Fifteen questions, one right answer each, a reason with the reference attached, and a score at the end that does not much matter. If a question annoys you, the reference is there so you can go and check; that is the intended outcome rather than a risk. The verse-by-verse habit that prevents this sort of drift is {a-reading-plan-you-actually-finish}.",
      links: {
        "a-reading-plan-you-actually-finish":
          "/blog/bible-in-a-year-on-your-phone",
      },
    },
    {
      type: "note",
      text: "Quotations and references are from the World English Bible, a public-domain translation. Where a number is disputed between manuscripts, the question says so rather than picking a side.",
    },
  ],
  questions: [
    {
      id: "wise-men",
      prompt: "How many wise men visited the infant Jesus?",
      options: [
        { text: "Three" },
        { text: "Twelve" },
        { text: "The Bible does not say", correct: true },
        { text: "Two" },
      ],
      because:
        "Matthew 2 says magi, plural, and lists three gifts — gold, frankincense and myrrh. The number three attached itself to the men because of the gifts, somewhere in the early centuries, and stayed.",
    },
    {
      id: "fruit",
      prompt: "What fruit did Adam and Eve eat in Eden?",
      options: [
        { text: "An apple" },
        { text: "A fig" },
        { text: "A pomegranate" },
        { text: "The text does not name a fruit", correct: true },
      ],
      because:
        "Genesis 3 calls it the fruit of the tree of the knowledge of good and evil, and never says which fruit. The apple is generally traced to a Latin pun — malum means both evil and apple — reinforced by centuries of painting.",
    },
    {
      id: "jonah",
      prompt: "What swallowed Jonah?",
      options: [
        { text: "A whale" },
        { text: "A great fish", correct: true },
        { text: "A sea serpent" },
        { text: "A leviathan" },
      ],
      because:
        "Jonah 1:17 says a great fish. The Greek in Matthew 12:40 is kētos, a large sea creature, which is not the modern zoological category either. Whale is a later translation choice, not the word.",
    },
    {
      id: "money",
      prompt: "Which is the actual wording in 1 Timothy 6:10?",
      options: [
        { text: "Money is the root of all evil" },
        {
          text: "The love of money is a root of all kinds of evil",
          correct: true,
        },
        { text: "Wealth is the root of all wickedness" },
        { text: "The rich shall not see the kingdom" },
      ],
      because:
        "Three changes in one sentence, and each one matters: the love of money rather than money, a root rather than the root, and all kinds of evil rather than all evil.",
    },
    {
      id: "ark",
      prompt: "How many people were on Noah's ark?",
      options: [
        { text: "Four" },
        { text: "Six" },
        { text: "Eight", correct: true },
        { text: "The number is not given" },
      ],
      because:
        "Noah, his wife, three sons and their three wives. 1 Peter 3:20 states the figure directly: eight souls were saved through water.",
    },
    {
      id: "help-themselves",
      prompt: "Where does “God helps those who help themselves” appear?",
      options: [
        { text: "Proverbs" },
        { text: "James" },
        { text: "Nowhere in the Bible", correct: true },
        { text: "Ecclesiastes" },
      ],
      because:
        "It is not scripture. The sentiment is ancient — a version appears in Aesop — and the English phrasing was popularised by Benjamin Franklin in Poor Richard's Almanack. Surveys regularly find it is the line most people believe is biblical.",
    },
    {
      id: "goliath",
      prompt:
        "After the stone struck him, what did David use to cut off Goliath's head?",
      options: [
        { text: "His own sword" },
        { text: "Goliath's sword", correct: true },
        { text: "A spear taken from a soldier" },
        { text: "The text does not say" },
      ],
      because:
        "1 Samuel 17:51 — David ran, stood over him, took Goliath's own sword and drew it from its sheath. Verse 50 notes there was no sword in David's hand at all.",
    },
    {
      id: "shortest",
      prompt: "Which is the shortest verse in most English Bibles?",
      options: [
        { text: "“Jesus wept.”", correct: true },
        { text: "“Rejoice always.”" },
        { text: "“Pray without ceasing.”" },
        { text: "“God is love.”" },
      ],
      because:
        "John 11:35, two words in English. In the Greek text the shortest is 1 Thessalonians 5:16, which English renders as three words — so the answer depends on which language you are counting in.",
    },
    {
      id: "baptism",
      prompt: "Who baptised Jesus?",
      options: [
        { text: "Peter" },
        { text: "John the Baptist", correct: true },
        { text: "Andrew" },
        { text: "Nobody — he was not baptised" },
      ],
      because:
        "Matthew 3, Mark 1 and Luke 3 all record it in the Jordan, with John initially objecting that the roles should be the other way round.",
    },
    {
      id: "denials",
      prompt: "How many times did Peter deny Jesus?",
      options: [
        { text: "Once" },
        { text: "Twice" },
        { text: "Three times", correct: true },
        { text: "Seven times" },
      ],
      because:
        "All four gospels record three, before the rooster. John 21 answers it with three questions on the beach, which is not a coincidence.",
    },
    {
      id: "lion-lamb",
      prompt: "Isaiah 11:6 — which animals actually lie down together?",
      options: [
        { text: "The lion and the lamb" },
        { text: "The wolf and the lamb", correct: true },
        { text: "The bear and the calf" },
        { text: "The eagle and the dove" },
      ],
      because:
        "The wolf lives with the lamb, the leopard lies down with the young goat, and a little child leads them. A lion appears later in the verse, eating straw with the ox. The lion-and-lamb pairing is a popular compression of the passage.",
    },
    {
      id: "innkeeper",
      prompt: "What does Luke's nativity say about the innkeeper?",
      options: [
        { text: "He turns them away" },
        { text: "He offers the stable" },
        { text: "There is no innkeeper in the text", correct: true },
        { text: "He is named as Simeon" },
      ],
      because:
        "Luke 2:7 says only that she laid him in a manger because there was no room for them in the inn. No innkeeper appears, no conversation is recorded, and no stable is mentioned either.",
    },
    {
      id: "chapters",
      prompt: "Which book of the Bible has the most chapters?",
      options: [
        { text: "Isaiah" },
        { text: "Genesis" },
        { text: "Psalms", correct: true },
        { text: "Matthew" },
      ],
      because:
        "150 psalms. Isaiah has 66 and Genesis 50. Psalms is also, by most counts, the longest book of the Bible by word count.",
    },
    {
      id: "first-witness",
      prompt: "Who is recorded as the first to see the risen Jesus?",
      options: [
        { text: "Peter" },
        { text: "Mary Magdalene", correct: true },
        { text: "John" },
        { text: "Thomas" },
      ],
      because:
        "John 20 and Mark 16:9 both name Mary Magdalene. In first-century legal practice a woman's testimony carried little weight, which is one reason historians find the detail striking — it is not what an invented account would put first.",
    },
    {
      id: "matthew-job",
      prompt: "What was Matthew's job before he followed Jesus?",
      options: [
        { text: "Fisherman" },
        { text: "Carpenter" },
        { text: "Tax collector", correct: true },
        { text: "Physician" },
      ],
      because:
        "Matthew 9:9 — sitting at the tax collection booth. Luke, by early tradition, is the physician; Peter, Andrew, James and John are the fishermen.",
    },
  ],
  outcomes: [
    {
      id: "start",
      name: "An honest starting point",
      min: 0,
      summary:
        "Most of these came from carols, films and repetition rather than from the text — which is exactly the situation this quiz was written for.",
      body: [
        "A low score on this particular set of questions is not a measure of faith or of attention. Nearly every miss here is a piece of received culture: the three kings from a Christmas card, the apple from a painting, the whale from a children's book, the innkeeper from a school nativity. They are held confidently because they were never learned from a page in the first place, so there was never a moment where they could have been checked.",
        "The useful thing to take from a low score is not shame but a method. The reason each answer above carries a reference is that the reference is the whole remedy — reading a passage in its own context, once, permanently overwrites a summary that has been repeated for thirty years.",
        "Start with narrative rather than with a system. The books that read most like books — Ruth, Jonah, Mark, Acts — are short, and each one can be finished in a sitting or two, which is worth more than a plan you abandon in Leviticus.",
      ],
      verse: {
        text: "Your word is a lamp to my feet, and a light for my path.",
        ref: "Psalm 119:105",
      },
      practice: [
        "Read the whole of Jonah this week. It is four short chapters and takes about twelve minutes.",
        "Pick the question here that surprised you most and read the chapter around it, not just the verse.",
        "Choose a fixed twenty minutes — the same time each day — rather than resolving to read more.",
      ],
    },
    {
      id: "solid",
      name: "Sunday school, well remembered",
      min: 6,
      summary:
        "The stories are solidly in place. The misses are the details that only surface if you have read the passages themselves rather than heard them told.",
      body: [
        "You know the narratives and you know the people. What this score usually separates is the difference between knowing a story and having read the paragraph it lives in — the number of people on the ark, the exact wording in Timothy, whose sword ended the fight in the valley. Those details are only ever picked up on the page.",
        "That is a good position to be in and a slightly precarious one, because a well-remembered story is very resistant to correction. The version you already have is fluent, and fluency feels like accuracy. The quickest way past it is reading a familiar passage in an unfamiliar translation, where the sentences do not slide by in the shape you expect.",
        "The next step is length rather than frequency. Reading a whole gospel in two or three sittings does something a verse a day cannot: it shows you the argument the writer was making, which is where most of the details you missed actually live.",
      ],
      verse: {
        text: "These were more noble than those in Thessalonica, in that they received the word with all readiness of mind, examining the Scriptures daily to see whether these things were so.",
        ref: "Acts 17:11",
      },
      practice: [
        "Read one whole gospel in a week — Mark is the shortest, about ninety minutes in total.",
        "Read a familiar chapter in a translation you never use, and mark the three sentences that come out differently.",
        "Look up every reference in this quiz you got wrong, in context. Twenty minutes, and they will not slip again.",
      ],
    },
    {
      id: "read",
      name: "You have actually read it",
      min: 10,
      summary:
        "You are answering from the text rather than from the culture around it, including on the questions designed to catch exactly that.",
      body: [
        "Ten or more here means you did not take the popular answer on at least two of the trick questions, which is the thing this quiz is really measuring. That comes from reading, and specifically from reading passages whole rather than encountering them as quotations.",
        "The interesting frontier from this point is not more facts but more context — why Matthew opens with a genealogy, why Mark is in such a hurry, why the detail about who reached the tomb first is preserved in all four accounts and reported differently in each. Those questions are where the text stops being a set of stories and starts being documents written by people with intentions.",
        "The practical risk at this level is that knowledge outruns habit. It is entirely possible to know the material well and not to have opened it in a month, and the two feel similar from the inside. Recall stays put for years after the reading stops, which is exactly what makes the gap so hard to notice.",
      ],
      verse: {
        text: "Every Scripture is God-breathed and profitable for teaching, for reproof, for correction, and for instruction in righteousness.",
        ref: "2 Timothy 3:16",
      },
      practice: [
        "Add one book of background — a short commentary or introduction — to the next book you read, rather than another reading plan.",
        "Teach one of these passages to somebody. Explaining it is where you find out what you actually understand.",
        "Check when you last read for thirty unbroken minutes. If the answer is not this week, that is the gap, not knowledge.",
      ],
    },
    {
      id: "sharp",
      name: "Fourteen or fifteen",
      min: 14,
      summary:
        "You knew the wolf was not a lion and that nobody counted the magi. There is not much this quiz can tell you.",
      body: [
        "At this score you are not remembering answers, you are recalling passages, and you spotted the questions built specifically to reward the popular version. That is a level of familiarity that takes years, and it is worth noticing rather than shrugging off.",
        "The thing worth saying to someone at this level is that the constraint is almost never information. Most people who can score fifteen here can also tell you, honestly, that the amount they read has quietly fallen over the last few years — not because they stopped caring but because the twenty minutes that used to be reading is now spent somewhere else, in pieces, on a screen. That is the ordinary shape of it and it has nothing to do with knowledge.",
        "If any of that lands, the more useful page on this site is not another quiz. It is the one about what happens to those twenty minutes — where they went, why the going was invisible at the time, and what actually gets them back. Knowing the text well and reading it rarely is a common combination and an uncomfortable one to say out loud.",
      ],
      verse: {
        text: "You search the Scriptures, because you think that in them you have eternal life; and these are they which testify about me.",
        ref: "John 5:39",
      },
      practice: [
        "Take the phone and faith test, which asks a harder question than this one did.",
        "Pick the book you have read least, not the one you know best, and give it a fortnight.",
        "Find one person to read with. Knowledge held alone tends to stay a hobby.",
      ],
    },
  ],
  after: [
    {
      type: "h2",
      id: "why-we-get-these-wrong",
      text: "Why nearly everyone misses the same ones",
    },
    {
      type: "p",
      text: "Because none of these errors came from reading. They came from art, hymns, school plays and the ordinary business of retelling — and a retelling optimises for memorability, not accuracy. Three kings is a better image than an unnumbered group of foreign astrologers. An apple is paintable. A whale is a thing a child can picture. Every one of these substitutions is an improvement in narrative terms and a small loss in accuracy, repeated for centuries.",
    },
    {
      type: "p",
      text: "The pattern is worth knowing for its own sake, because it applies well beyond the questions here. Anything you know about scripture that you cannot remember reading is worth checking once. It usually takes two minutes and the results are surprising more often than they are not.",
    },
    {
      type: "h2",
      id: "how-to-fix-it",
      text: "The habit that prevents it",
    },
    {
      type: "p",
      text: "Reading whole units. Not verses, and not a scattered plan — a book, or at minimum a chapter with its context, in one sitting. The passages that get remembered wrongly are always the ones that circulate as fragments, and a fragment cannot correct itself. Twenty unbroken minutes is enough for a chapter of anything and most of Jonah.",
    },
    {
      type: "p",
      text: "The obstacle to twenty unbroken minutes is rarely time and almost always interruption. That is the problem Bible Shield was built for: it closes the apps that take the evening at the times you choose and asks for a short prayer before it opens them again, and it carries the whole Bible with search, highlights and notes on the same device. {The-reading-plan-piece} covers what actually gets finished and what does not.",
      links: {
        "The-reading-plan-piece": "/blog/bible-in-a-year-on-your-phone",
      },
    },
    {
      type: "note",
      text: "Every reference above is checkable in about a minute. That is the intended use of this page rather than the score.",
    },
  ],
  faq: [
    {
      q: "Are the answers here disputed by any tradition?",
      a: "The factual ones are not — they are matters of what the text says. Two questions have genuine textual nuance and the reasons say so: the shortest verse depends on whether you count in English or Greek, and Matthew's Greek for what swallowed Jonah names a large sea creature, not a species.",
    },
    {
      q: "Which translation is this based on?",
      a: "The World English Bible, which is in the public domain. None of the answers change between mainstream translations; the wording of the quotations does.",
    },
    {
      q: "Can I use these questions for a church group?",
      a: "Yes, and the reason lines are written to be read out. They work better as a discussion opener than as a competition, because the interesting part of each one is why the popular answer became popular.",
    },
    {
      q: "Is a low score a problem?",
      a: "No. Almost every wrong answer here is inherited rather than careless — the fruit, the number of magi, the innkeeper. Nobody learned those from a page, which is precisely why they were never checked.",
    },
  ],
  related: [
    "bible-in-a-year-on-your-phone",
    "morning-quiet-time-routine",
    "phone-addiction-bible-verses",
  ],
  relatedQuizzes: ["which-bible-character-are-you", "phone-and-faith-test"],
};
